import {getTestProps} from '@benchling/js-test-utils';
import {Anchor, Group, Paper, Stack, Text, createStyles} from '@mantine/core';
import React from 'react';

import {ChatRenderContext} from 'llm/chat-library/ChatRenderContext';
import {WebSearchResult} from 'llm/chat-library/tool-details/WebSearchToolCallParser';
import {getWebCitationUrlParts} from 'llm/markdown/LLMMarkdownUtils';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const useStyles = createStyles((theme) => ({
  list: {
    maxHeight: 360,
    overflowY: 'auto',
  },
  card: {
    borderColor: theme.colors.gray[3],
  },
  title: {
    fontWeight: 600,
  },
  metaIcon: {
    fontSize: 12,
    color: theme.colors.gray[6],
    flexShrink: 0,
  },
}));

function WebSearchResultCard({
  result,
  index,
  isExportRender,
}: {
  result: WebSearchResult;
  index: number;
  isExportRender: boolean;
}): JSX.Element {
  const {classes} = useStyles();
  const urlParts = getWebCitationUrlParts(result.url);
  // Drop a leading `www.` for a cleaner source label.
  const domain = urlParts != null ? urlParts.domain.replace(/^www\./, '') : null;
  const title = result.title !== '' ? result.title : (domain ?? result.url);
  const metaParts = [domain, result.publishedDate].filter(
    (part): part is string => part != null && part !== ''
  );

  return (
    <Paper withBorder={true} radius="sm" p="sm" className={classes.card}>
      <Stack spacing={4}>
        {urlParts != null ? (
          <Anchor
            {...getTestProps({
              component: 'WebSearchResults',
              element: 'resultLink',
              index: String(index),
            })}
            href={urlParts.href}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            {...(isExportRender ? {} : {lineClamp: 2})}
            className={classes.title}
          >
            {title}
          </Anchor>
        ) : (
          <Text size="sm" {...(isExportRender ? {} : {lineClamp: 2})} className={classes.title}>
            {title}
          </Text>
        )}
        {metaParts.length > 0 && (
          <Group spacing={6} noWrap={true}>
            <BenchlingIcon icon="globe" className={classes.metaIcon} />
            <Text size="xs" color="dimmed" truncate={true}>
              {metaParts.join(' · ')}
            </Text>
          </Group>
        )}
        {result.text !== '' && (
          <Text size="xs" color="gray.7" {...(isExportRender ? {} : {lineClamp: 3})}>
            {result.text}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

export interface WebSearchResultsProps {
  results: Array<WebSearchResult>;
}

/** Renders WebSearch results as a list of clickable source cards. */
export function WebSearchResults({results}: WebSearchResultsProps): JSX.Element {
  const {isExportRender} = ChatRenderContext.useContext();
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  return (
    <Stack spacing="xs" className={isExportRender ? undefined : classes.list}>
      {results.map((result, index) => (
        <WebSearchResultCard
          key={`${result.url}-${index}`}
          result={result}
          index={index}
          isExportRender={isExportRender}
        />
      ))}
    </Stack>
  );
}
